import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EDataTableType, EModuleType, EReportType } from '../../data/Constants';
import { MaintenanceTable } from '../DataTable/DataTable';
import { getReports, resetReports, createReport } from '../../store/states/ReportSlice';
import { ReportApi } from '../../api/ReportApi';
import { useDispatch } from 'react-redux';
import { setAlertMessage } from '../../store/states/AlertMessageSlice';
import { AppDispatch, AppStore } from '../../store';
import type { IAPIResponse, IManagementProps } from '../../../types';

/**
 * Handle the outcome of a COE report generation request.
 *
 * @param res - API response returned by the generate report thunk
 * @param failMessage - Fallback message shown when the response carries no error message
 * @param successMessage - Message shown when the report was generated
 * @param dispatch - Redux dispatch function
 */
function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  dispatch: AppDispatch
): void {
  const data = AppStore.getState().reports;
  if (res?.data) {
    dispatch(
      getReports({
        page: data.currentPage,
        limit: data.recordsPerPage,
        searchCode: data.searchKey,
        reportType: EDataTableType.Coe,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
  } else {
    const message = res?.error?.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message, type: 'error' }));
  }
}

/**
 * COE report screen with the report history table and a generate control.
 *
 * @param props - Component props
 * @param props.token - Auth token forwarded to the maintenance table
 * @returns The COE report view, or the API error element when a report request failed
 */
export const COEManagement = (props: IManagementProps): React.JSX.Element | null => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isReset, setIsReset] = useState(false);

  // The report state is shared by every report type, so clear the previously loaded report
  // before the table mounts; otherwise the table reuses the cached rows and skips its first read
  useEffect(() => {
    dispatch(resetReports());
    setIsReset(true);
    return () => {
      dispatch(resetReports());
    };
  }, [dispatch]);

  /**
   * Trigger generation of a new COE report and handle the response
   */
  const handleGenerateReport = (): void => {
    void dispatch(createReport(EReportType.Coe)).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.create'),
        t('messages.success.create'),
        dispatch
      );
    });
  };

  /**
   * Download the generated COE report file
   *
   * @param param - Object containing the report history id
   */
  const handleDownloadReport = (param: { id: string }): void => {
    ReportApi.downloadReport({ id: param.id, reportType: EReportType.Coe });
  };

  return (
    <>
      <h3 className="mainHeader">{t('labels.coe')}</h3>
      {isReset && (
        <MaintenanceTable
          token={props.token}
          type={EDataTableType.Coe}
          getDataList={getReports}
          moduleType={EModuleType.Reports}
          generateReport={handleGenerateReport}
          download={handleDownloadReport}
        />
      )}
    </>
  );
};
